import React, { useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { ListingTable } from "components/listing";
import { Button } from "components/buttons";
import { Modal } from "components/notifications";
import { ShowHide } from "components/layout";
import { useUserHasAccess } from "hooks/useUserHasAccess";

const defaultArray = [];
const defaultFunction = () => { };

// The small-operator conditions count raw offences rather than breached KPI thresholds, so an alert
// can legitimately arrive with no breaches behind it. Each stream has its own condition.
const smallOperatorConditionKeys = ["SMALL_OPERATOR_TWO_IN_PERIOD", "SMALL_FLEET_TWO_IN_PERIOD"];

// The drivers KPIs count tachograph days, the maintenance KPIs count fleet size, so the
// small-operator provision is worded per stream.
const smallOperatorExplanations = {
    Drivers: "A period reporting fewer than 25 tachograph days is allowed one offence. This period holds more than one, so an alert was raised.",
    Maintenance: "A fleet of fewer than 20 vehicles including trailers is allowed one failure in a period. This period holds more than one, so an alert was raised."
};

// Builds the popup summary line, e.g. "An alert was created because you received a total of 5 yellow
// threshold breaches, these breaches occurred in the following places:". When the breaches are a mix
// of severities the colour word is dropped.
function buildAlertSummaryText(alert) {
    const breachCount = alert.breaches.length;
    const breachTypes = new Set(alert.breaches.map((breach) => breach.breachType));

    let typeText = "threshold breaches";
    if (breachTypes.size === 1) {
        typeText = `${alert.breaches[0].breachType.toLowerCase()} threshold breaches`;
    }

    return `An alert was created because you received a total of ${breachCount} ${typeText}, these breaches occurred in the following places:`;
}

export function ErAlerts(
    {
        alerts = defaultArray,
        loading = false,
        onSelectAlert = defaultFunction,
        selectedAlertId = null,
        noDataMessage = "There are no driver alerts for the selected period.",
        stream = "Drivers"
    }
) {
    const userHasAccess = useUserHasAccess();
    // The alert whose trigger-detail popup is open. Local state — the popup is self-contained and
    // does not affect the KPI grid (unlike the eye-icon highlight, which the parent owns).
    const [infoAlert, setInfoAlert] = useState(null);

    // Section-level access guard (PBI 160897): the alerts section must not render for users without
    // the Internal action claim, on top of the route-level guard on /earned-recognition.
    if (!userHasAccess("Internal")) {
        return <React.Fragment />;
    }

    const headers = ["ISO REPORTING PERIOD", "ALERT TRIGGER", "DVSA ALERTED", "ACTIONS"];

    const rows = alerts.map((alert) => {
        const actions = (
            <React.Fragment>
                <Button
                    minimal={true}
                    icon="info-sign"
                    onClick={() => setInfoAlert(alert)}
                    className="er-alert-info"
                />
                <Button
                    minimal={true}
                    icon="eye-open"
                    onClick={() => onSelectAlert(alert)}
                    className={classNames("er-alert-eye", { "er-alert-eye-active": alert.id === selectedAlertId })}
                />
            </React.Fragment>
        );

        return [
            `${alert.isoPeriod}`,
            alert.triggerDescription,
            alert.dvsaAlerted ? "Yes" : "No",
            actions
        ];
    });

    const breachRows = infoAlert != null
        ? infoAlert.breaches.map((breach) => {
            return [
                `Period ${breach.isoPeriod} ${breach.isoYear}`,
                breach.kpiLabel,
                breach.breachType,
                `${breach.percentage}%`
            ];
        })
        : [];

    return (
        <React.Fragment>
            <h2>Alerts</h2>
            <ListingTable
                loadingData={loading}
                data={rows}
                headers={headers}
                totalRecordCount={rows.length}
                pageable={false}
                sortable={false}
                noDataHeading="No alerts"
                noDataMessage={noDataMessage}
            />
            <Modal
                title={infoAlert != null ? `Period ${infoAlert.isoPeriod} Trigger` : ""}
                isOpen={infoAlert != null}
                onClose={() => setInfoAlert(null)}
                size="large"
            >
                <TriggerDetailBody infoAlert={infoAlert} breachRows={breachRows} stream={stream} />
            </Modal>
        </React.Fragment>
    );
}

ErAlerts.propTypes = {
    alerts: PropTypes.array,
    loading: PropTypes.bool,
    onSelectAlert: PropTypes.func,
    selectedAlertId: PropTypes.number,
    noDataMessage: PropTypes.string,
    stream: PropTypes.oneOf(["Drivers", "Maintenance"])
};

// Body of the trigger-detail popup: the summary line plus the breach breakdown table. A small-operator
// alert has no breaches to break down, so it explains the rule that fired instead. The branch is on the
// condition key, not on an empty breach list, because any other condition alerting with no breaches is a
// fault worth surfacing.
function TriggerDetailBody({ infoAlert = null, breachRows = defaultArray, stream = "Drivers" }) {
    if (infoAlert == null) {
        return <React.Fragment />;
    }

    const isSmallOperatorAlert = smallOperatorConditionKeys.includes(infoAlert.triggerCondition);

    let summaryText = smallOperatorExplanations[stream];
    if (!isSmallOperatorAlert) {
        summaryText = buildAlertSummaryText(infoAlert);
    }

    return (
        <React.Fragment>
            <p>{summaryText}</p>
            <ShowHide
                evaluator={!isSmallOperatorAlert}
                show={(
                    <ListingTable
                        loadingData={false}
                        data={breachRows}
                        headers={["PERIOD", "KPI", "BREACH TYPE", "BREACH %"]}
                        totalRecordCount={breachRows.length}
                        pageable={false}
                        sortable={false}
                    />
                )}
            />
        </React.Fragment>
    );
}

TriggerDetailBody.propTypes = {
    infoAlert: PropTypes.object,
    breachRows: PropTypes.array,
    stream: PropTypes.oneOf(["Drivers", "Maintenance"])
};
